import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

const arwayEscape = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' })[char]);
const arwayMoney = (value) => {
  const amount = Number(value || 0);
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'ro-RO', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'RON',
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (_) {
    return `${amount.toFixed(2)} lei`;
  }
};

const arwayProductImage = (product) => {
  const image = product?.featured_image || product?.image;
  if (!image) return '';
  if (typeof image === 'string') return image;
  return image.url || image.src || '';
};

const arwayProductPrice = (product) =>
  product?.price_min ?? product?.price ?? product?.variants?.[0]?.price ?? 0;

// ARWAY strict relevance layer.
// Shopify predictive search intentionally has typo tolerance. That's useful in general,
// but it can also return unrelated catalogue items when a full word has no good hit.
// We keep Shopify for candidate discovery, then only display products whose own searchable
// data actually contains what the customer typed.
const arwayNormalize = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');

const arwayCompact = (value) => arwayNormalize(value).replace(/\s+/g, '');

const arwayProductSearchText = (product) => {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const parts = [
    product?.title,
    product?.vendor,
    product?.type,
    product?.product_type,
    product?.handle,
    ...(Array.isArray(product?.tags) ? product.tags : []),
    ...variants.flatMap((variant) => [variant?.title, variant?.sku, variant?.barcode]),
  ];

  return arwayNormalize(parts.filter(Boolean).join(' '));
};

const arwayStrictMatch = (product, rawQuery) => {
  const query = arwayNormalize(rawQuery);
  if (!query) return false;

  const text = arwayProductSearchText(product);
  if (!text) return false;

  const compactQuery = arwayCompact(rawQuery);
  const compactText = text.replace(/\s+/g, '');

  // Handles brand/model spellings such as "CF MOTO" vs "CFMOTO" and codes with dashes/spaces.
  if (compactQuery.length >= 2 && compactText.includes(compactQuery)) return true;

  // Every typed token must genuinely exist somewhere in the product's own searchable fields.
  const tokens = query.split(' ').filter(Boolean);
  return tokens.length > 0 && tokens.every((token) => text.includes(token));
};

const arwayRelevanceScore = (product, rawQuery) => {
  const query = arwayNormalize(rawQuery);
  const compactQuery = arwayCompact(rawQuery);
  const title = arwayNormalize(product?.title);
  const compactTitle = title.replace(/\s+/g, '');
  const vendor = arwayNormalize(product?.vendor);
  const type = arwayNormalize(product?.type || product?.product_type);
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const variantText = arwayNormalize(variants.flatMap((variant) => [variant?.title, variant?.sku, variant?.barcode]).filter(Boolean).join(' '));

  let score = 0;
  if (title === query) score += 1000;
  if (title.startsWith(query)) score += 500;
  if (compactQuery && compactTitle.startsWith(compactQuery)) score += 450;
  if (title.includes(query)) score += 300;
  if (compactQuery && compactTitle.includes(compactQuery)) score += 260;
  if (vendor === query) score += 240;
  if (vendor.startsWith(query)) score += 180;
  if (type === query) score += 170;
  if (type.startsWith(query)) score += 130;
  if (variantText.includes(query)) score += 120;

  const tokens = query.split(' ').filter(Boolean);
  score += tokens.reduce((sum, token) => sum + (title.includes(token) ? 35 : 0), 0);
  score += tokens.reduce((sum, token) => sum + (vendor.includes(token) ? 20 : 0), 0);
  score += tokens.reduce((sum, token) => sum + (variantText.includes(token) ? 15 : 0), 0);

  return score;
};

const arwayMergeProducts = (...groups) => {
  const seen = new Set();
  const merged = [];

  for (const group of groups) {
    for (const product of group || []) {
      const key = String(product?.id || product?.url || product?.handle || product?.title || '');
      if (!key || seen.has(key)) continue;
      seen.add(key);
      merged.push(product);
    }
  }

  return merged;
};


/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce(() => {
    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 180);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    const { predictiveSearchResults, searchInput } = this.refs;
    const abortController = this.#createAbortController();
    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query: searchTerm },
        promise: deferredPromise.promise,
      })
    );

    const fetchProducts = async (term) => {
      const route = Theme.routes.predictive_search_url || `${window.Shopify?.routes?.root || '/'}search/suggest`;
      const jsonRoute = route.endsWith('.json') ? route : `${route}.json`;
      const url = new URL(jsonRoute, location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set(
        'resources[options][fields]',
        'title,product_type,tag,variants.title,vendor,variants.sku,variants.barcode'
      );

      const response = await fetch(url, {
        signal: abortController.signal,
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      const responseText = await response.text();
      if (!response.ok) {
        throw new Error(`Predictive search HTTP ${response.status}: ${responseText.slice(0, 160)}`);
      }

      const data = JSON.parse(responseText);
      return Array.isArray(data?.resources?.results?.products) ? data.resources.results.products : [];
    };

    try {
      // 1) Ask Shopify for the exact typed query.
      const exactCandidates = await fetchProducts(searchTerm);
      if (abortController.signal.aborted) return;
      if (this.refs.searchInput.value.trim() !== searchTerm) return;

      let candidates = exactCandidates;
      let products = candidates.filter((product) => arwayStrictMatch(product, searchTerm));

      // 2) Shopify's typo tolerance can occasionally replace a perfectly valid full word
      // with fuzzy matches. If strict matching found too few genuine products, ask once more
      // using the immediately previous prefix, merge the candidates, then STILL filter using
      // the customer's complete query. This fixes cases such as "ule" -> correct and
      // "ulei" -> unrelated, without hard-coding any product/category/brand.
      const normalizedQuery = arwayNormalize(searchTerm);
      if (products.length < 6 && normalizedQuery.length >= 4) {
        const fallbackTerm = searchTerm.trim().slice(0, -1).trim();
        if (fallbackTerm.length >= 2) {
          const fallbackCandidates = await fetchProducts(fallbackTerm);
          if (abortController.signal.aborted) return;
          if (this.refs.searchInput.value.trim() !== searchTerm) return;
          candidates = arwayMergeProducts(candidates, fallbackCandidates);
          products = candidates.filter((product) => arwayStrictMatch(product, searchTerm));
        }
      }

      // 3) Stable relevance order: exact title / title prefix / brand / model / SKU first.
      products = products
        .sort((a, b) => arwayRelevanceScore(b, searchTerm) - arwayRelevanceScore(a, searchTerm))
        .slice(0, 8);

      const productMarkup = products.length
        ? `<section class="arway-predictive-group"><div class="arway-predictive-head"><strong>Produse</strong><span>${products.length}</span></div><div class="arway-predictive-products predictive-search-results__list">${products
            .map((product) => {
              const image = arwayProductImage(product);
              const price = arwayProductPrice(product);
              const meta = [product.vendor, product.type || product.product_type].filter(Boolean).join(' · ');
              return `<a class="arway-predictive-product" href="${arwayEscape(product.url)}" ref="resultsItems[]" role="option"><span class="arway-predictive-product__media">${image ? `<img src="${arwayEscape(image)}" alt="${arwayEscape(product.title)}" loading="lazy" width="80" height="80">` : ''}</span><span class="arway-predictive-product__copy"><strong>${arwayEscape(product.title)}</strong>${meta ? `<small>${arwayEscape(meta)}</small>` : ''}<b>${arwayMoney(price)}</b></span></a>`;
            })
            .join('')}</div></section>`
        : '';

      predictiveSearchResults.innerHTML = products.length
        ? `<div id="predictive-search-results" class="predictive-search-dropdown arway-predictive-results" role="listbox"><div class="arway-predictive-results__inner" data-search-results>${productMarkup}</div></div>`
        : `<div id="predictive-search-results" class="predictive-search-dropdown arway-predictive-results" role="listbox"><div class="arway-predictive-empty predictive-search-results__no-results" data-search-results>Nu am găsit produse care să corespundă exact cu <strong>${arwayEscape(searchTerm)}</strong>.<a href="${Theme.routes.search_url}?q=${encodeURIComponent(searchTerm)}">Vezi căutarea completă</a></div></div>`;

      searchInput.setAttribute('aria-expanded', 'true');
      this.#resetScrollPositions();
      deferredPromise.resolve({ totalCount: products.length });
    } catch (error) {
      if (abortController.signal.aborted) {
        deferredPromise.reject(new Error('Fetch aborted by user'));
        return;
      }

      if (this.refs.searchInput.value.trim() === searchTerm) {
        predictiveSearchResults.innerHTML = `<div id="predictive-search-results" class="predictive-search-dropdown arway-predictive-results"><div class="arway-predictive-empty predictive-search-results__no-results">Căutarea nu a putut fi încărcată. <a href="${Theme.routes.search_url}?q=${encodeURIComponent(searchTerm)}">Caută în catalog</a></div></div>`;
      }
      deferredPromise.resolve({ totalCount: 0 });
      console.warn('[ARWAY] Predictive search error:', error);
    }
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    this.#currentIndex = -1;
    searchInput.value = '';
    searchInput.setAttribute('aria-expanded', 'false');
    this.#hideResetButton();
    this.#activeFetch?.abort();
    predictiveSearchResults.innerHTML = `<div id="predictive-search-results" class="predictive-search-dropdown arway-predictive-results"><div class="arway-predictive-empty"><strong>Caută în catalogul ARWAY</strong><span>Scrie denumirea produsului, marca, modelul sau codul SKU.</span></div></div>`;
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
